import { useMemo } from "react";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { ToolEditorPageKey } from "../ToolEditorProvider.store";
import { useFormikContext } from "formik";

/**
 * Hook to derive tool editor timeline status from store state
 */
export const useToolEditorTimelineStatus = (page: ToolEditorPageKey) => {
  const hasDraftSpec = useToolEditorContext((s) => s.draftSpec !== null);
  const hasTestTable = useToolEditorContext((s) => s.testTable !== null);
  const uploadStatus = useToolEditorContext((s) => s.codeUploadStatus);
  const saveStatus = useToolEditorContext((s) => s.draftSpecSaveStatus);
  const isDraftSpecSynced = useToolEditorContext((s) => s.isDraftSpecSynced);
  const isCreatingTestTable = useToolEditorContext(
    (s) => s.isCreatingTestTable,
  );

  const isContainerImageWarning = useToolEditorContext(
    (s) => s.isContainerImageWarning,
  );

  const { isSubmitting: isPublishing } = useFormikContext();

  const timelineStatusByPage = useMemo(() => {
    const map: Record<
      ToolEditorPageKey,
      {
        status:
          | "current"
          | "warning"
          | "disabled"
          | "incomplete"
          | "loading"
          | "danger"
          | "complete";
        isDisabled: boolean;
      }
    > = {
      CODE: { status: "disabled", isDisabled: false },
      SPEC: { status: "disabled", isDisabled: true },
      TEST: { status: "disabled", isDisabled: true },
      PUBLISH: { status: "disabled", isDisabled: true },
    };

    if (hasDraftSpec) {
      /** We have code */
      map["CODE"] = { status: "complete", isDisabled: false };
      map["SPEC"] = { status: "incomplete", isDisabled: false };
      map["TEST"] = { status: "incomplete", isDisabled: false };
    }

    if (hasTestTable) {
      /**
       * TODO: We may have a requirement for users to run a task
       * or other conditions to fulfill before this is considered complete
       */
      map["TEST"] = { status: "complete", isDisabled: false };
      map["SPEC"] = { status: "complete", isDisabled: false };
      map["PUBLISH"] = { status: "incomplete", isDisabled: false };

      if (!isDraftSpecSynced) {
        map["TEST"] = {
          ...map["TEST"],
          status: "warning",
        };
      }
    }

    /**
     * Disable all pages except code while upload in progress
     */
    if (uploadStatus === "UPLOADING") {
      (["SPEC", "TEST", "PUBLISH"] as const).forEach((pageKey) => {
        map[pageKey].isDisabled = true;
      });
    }

    /**
     * Disable all pages except spec if unsaved changes
     */
    if (saveStatus !== "SAVED") {
      (["CODE", "TEST", "PUBLISH"] as const).forEach((pageKey) => {
        map[pageKey].isDisabled = true;
      });
    }
    /** Set current page */
    map[page] = { status: "current", isDisabled: false };

    /** handle loading states */
    if (isCreatingTestTable) {
      map["TEST"].status = "loading";
    }

    if (isPublishing) {
      map["PUBLISH"].status = "loading";
    }

    /**
     * Handle case where container image is deleted/deprecated or user has lost access after
     */

    if (isContainerImageWarning) {
      for (const _key in map) {
        const key = _key as keyof typeof map;
        // if it's not already disabled, show a status of warning
        if (!map[key].isDisabled) {
          map[key].status = "warning";
        }
      }
    }

    return map;
  }, [
    isContainerImageWarning,
    hasDraftSpec,
    hasTestTable,
    isCreatingTestTable,
    isDraftSpecSynced,
    isPublishing,
    page,
    saveStatus,
    uploadStatus,
  ]);

  return timelineStatusByPage;
};
